import { InfiniteData, useQueryClient } from "@tanstack/react-query";
import { isArray, isEqual } from "lodash";
import { useCallback } from "react";

function isInfiniteData<T>(data: unknown): data is InfiniteData<T[]> {
  return (
    typeof data === "object" &&
    data !== null &&
    "pages" in data &&
    "pageParams" in data &&
    Array.isArray((data as InfiniteData<T>).pages) &&
    Array.isArray((data as InfiniteData<T>).pageParams)
  );
}

export const useQueryUpdater = <TResult>({
  queryKey,
}: {
  queryKey: string;
}) => {
  const queryClient = useQueryClient();

  const getQueriesStartsWith = useCallback(
    (queryKey: string) => {
      return queryClient
        .getQueryCache()
        .findAll()
        .filter(
          (query) =>
            isArray(query.queryKey) &&
            isEqual(query.queryKey.slice(0, 1), [queryKey])
        );
    },
    [queryClient]
  );

  const updateEntity = useCallback(
    (converter: (data: TResult) => TResult) => {
      const queries = getQueriesStartsWith(queryKey);

      for (const query of queries) {
        const data = query.state.data;

        if (isInfiniteData<TResult>(data)) {
          const newPage = data.pages.flat().map(converter);

          queryClient.setQueryData(query.queryKey, {
            pageParams: [{ offset: 0, limit: newPage.length }],
            pages: [newPage],
          });

          continue;
        }

        if (isArray(data)) {
          queryClient.setQueryData(query.queryKey, (data: any) =>
            data.map(converter)
          );
          continue;
        }

        if (typeof data === "object") {
          queryClient.setQueryData(query.queryKey, converter(data as TResult));
        }
      }

      queryClient.invalidateQueries({ queryKey: [queryKey] });
    },
    [getQueriesStartsWith, queryClient, queryKey]
  );

  const deleteEntity = useCallback(
    (filter: (data: TResult) => boolean) => {
      const queries = getQueriesStartsWith(queryKey);

      for (const query of queries) {
        const data = query.state.data;

        if (isInfiniteData<TResult>(data)) {
          const newPage = data.pages.flat().filter(filter);

          queryClient.setQueryData(query.queryKey, {
            pageParams: [{ offset: 0, limit: newPage.length }],
            pages: [newPage],
          });

          continue;
        }

        if (isArray(data)) {
          queryClient.setQueryData(query.queryKey, (data: any) =>
            data.filter(filter)
          );
          continue;
        }

        if (typeof data === "object" && !filter(data as TResult)) {
          queryClient.removeQueries({ queryKey: query.queryKey });
        }
      }

      queryClient.invalidateQueries({ queryKey: [queryKey] });
    },
    [getQueriesStartsWith, queryClient, queryKey]
  );

  return { updateEntity, deleteEntity };
};
